import React, {lazy, Suspense} from "react";
import ReactDOM from "react-dom/client";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import {createTheme} from "./theme/create-theme";
import {ThemeProvider} from "@mui/material/styles";
import './theme/index.css';
import {ToastContainer} from "react-toastify";
import {registerLicense} from '@syncfusion/ej2-base';
import './theme/font.css';
// Keep ErrorPage as regular import (it's small and needed for error handling)
import ErrorPage from "./pages/error-page";

registerLicense('Ngo9BigBOggjHTQxAR8/V1JEaF5cXmRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWXhecXVTRWFYUERzWEtWYEk=');


// ✅ Lazy load all route components
// Home pages
const Home = lazy(() => import("./pages/home"));
const Pricing = lazy(() => import("./pages/pricing"));
const ContactUs = lazy(() => import("./pages/contact-us"));
const Features = lazy(() => import("./pages/features"));
const TermsAndConditions = lazy(() => import("./pages/terms-and-conditions"));

// Login/Register
const Register = lazy(() => import("./pages/register"));
const Login = lazy(() => import("./pages/login"));
const ResetPassword = lazy(() => import("./pages/reset-password"));

// Dashboard pages
const Dashboard = lazy(() => import("./dashboard/dashboard"));
const Rewards = lazy(() => import("./dashboard/rewards"));
const PublishCenter = lazy(() => import("./dashboard/publishing-center"));
const Customers = lazy(() => import("./dashboard/customers"));
const Employees = lazy(() => import("./dashboard/employees"));
const MyMenu = lazy(() => import("./dashboard/my-menu"));
const Statistics = lazy(() => import("./dashboard/statistics"));
const Upgrade = lazy(() => import("./dashboard/upgrade"));
const MarketingPushNotifications = lazy(() => import("./dashboard/marketing-push-notifications"));
const MarketingSMS = lazy(() => import("./dashboard/marketing-sms"));
const MarketingVouchers = lazy(() => import("./dashboard/marketing-vouchers"));
const MyApp = lazy(() => import("./dashboard/myapp"));
const Settings = lazy(() => import("./dashboard/settings"));
const Help = lazy(() => import("./dashboard/help"));

const Checkout = lazy(() => import("./dashboard/checkout"));
const EditPayment = lazy(() => import("./dashboard/edit-payment"));
const SubscriptionComplete = lazy(() => import("./dashboard/subscription-complete"));

// Wrapper component to add Suspense to each route
const LazyRoute = ({ component: Component }) => (
    <Suspense fallback={null}>
        <Component />
    </Suspense>
);

const router = createBrowserRouter([
    // Home pages
    { path: "/", element: <LazyRoute component={Home} />, errorElement: <ErrorPage /> },
    { path: "/pricing", element: <LazyRoute component={Pricing} />, errorElement: <ErrorPage /> },
    { path: "/contact", element: <LazyRoute component={ContactUs} />, errorElement: <ErrorPage /> },
    { path: "/features", element: <LazyRoute component={Features} />, errorElement: <ErrorPage /> },

    // Login/Register
    { path: "/register", element: <LazyRoute component={Register} />, errorElement: <ErrorPage /> },
    { path: "/login", element: <LazyRoute component={Login} />, errorElement: <ErrorPage /> },
    { path: "/reset", element: <LazyRoute component={ResetPassword} />, errorElement: <ErrorPage /> },

    // Dashboard
    { path: "/dashboard", element: <LazyRoute component={Dashboard} />, errorElement: <ErrorPage /> },
    { path: "/rewards", element: <LazyRoute component={Rewards} />, errorElement: <ErrorPage /> },
    { path: "/publish", element: <LazyRoute component={PublishCenter} />, errorElement: <ErrorPage /> },
    { path: "/customers", element: <LazyRoute component={Customers} />, errorElement: <ErrorPage /> },
    { path: "/employees", element: <LazyRoute component={Employees} />, errorElement: <ErrorPage /> },
    { path: "/mymenu", element: <LazyRoute component={MyMenu} />, errorElement: <ErrorPage /> },
    { path: "/statistics", element: <LazyRoute component={Statistics} />, errorElement: <ErrorPage /> },
    { path: "/upgrade", element: <LazyRoute component={Upgrade} />, errorElement: <ErrorPage /> },
    { path: "/marketing/push-notifications", element: <LazyRoute component={MarketingPushNotifications} />, errorElement: <ErrorPage /> },
    { path: "/marketing/sms", element: <LazyRoute component={MarketingSMS} />, errorElement: <ErrorPage /> },
    { path: "/marketing/vouchers", element: <LazyRoute component={MarketingVouchers} />, errorElement: <ErrorPage /> },
    { path: "/myapp", element: <LazyRoute component={MyApp} />, errorElement: <ErrorPage /> },
    { path: "/settings", element: <LazyRoute component={Settings} />, errorElement: <ErrorPage /> },
    { path: "/help", element: <LazyRoute component={Help} />, errorElement: <ErrorPage /> },

    { path: "/checkout", element: <LazyRoute component={Checkout} />, errorElement: <ErrorPage /> },
    { path: "/editpayment", element: <LazyRoute component={EditPayment} />, errorElement: <ErrorPage /> },
    { path: "/complete", element: <LazyRoute component={SubscriptionComplete} />, errorElement: <ErrorPage /> },

    // Other
    { path: "/terms", element: <LazyRoute component={TermsAndConditions} />, errorElement: <ErrorPage /> },
]);

const theme = createTheme();
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <ThemeProvider theme={theme}>
        <RouterProvider router={router} />
        <ToastContainer />
    </ThemeProvider>
);

reportWebVitals();